import React from 'react'
import styled from 'styled-components'
import CircleNumericInput from '../../../../../generic/inputs/CircleNumericInput'
import SearchBlockWithTitle from '../../procedures/Q8/Layouts/SearchBlockWithTitle'
import { IProblemListItem } from '../../../../../modules/API/TeudotProblems/models/problems'
import { getProblemTypeAllowances, getInputFieldTitles } from '../utils/problemTypeUtils'
import { IProblemFormData } from '../types/problemModal'

/**
 * Props interface for ProblemInputFields component
 */
interface IProblemInputFieldsProps {
  problem: IProblemFormData
  problemTypes: IProblemListItem[] | null
  onProblemChange: (id: string, field: keyof IProblemFormData, value: string | number) => void
  disabled: boolean
}

/**
 * Component for rendering numeric input fields for problem data entry.
 * Inputs are enabled/disabled based on problem type allowances.
 * Hidden completely when no problem type is selected.
 * 
 * @param problem - The problem data object
 * @param problemTypes - Available problem types with allowance configuration
 * @param onProblemChange - Callback function for handling input changes
 */
const  ProblemInputFields: React.FC<IProblemInputFieldsProps> = ({
  problem,
  problemTypes,
  onProblemChange,
  disabled
}) => {
  // Don't render inputs if no problem type is selected
  if (problem.Type <= 0) {
    return null
  }
  const {
    WeightRequested,
    QuantityRequested,
    PriceRequested,
    Weight,
    Price,
    Quantity,
  } = problem
  const { weightAllowed, quantityAllowed, priceAllowed } = getProblemTypeAllowances(problem.Type, problemTypes)

  return (
    <SearchBlockWithTitle title={getInputFieldTitles({ weightAllowed, quantityAllowed, priceAllowed })}>
      <InputsWrapper>
        {/* Price input - disabled if not allowed for selected problem type */}
        <CircleNumericInput
          value={PriceRequested}
          changeFunc={(value: number) => onProblemChange(problem.id, 'PriceRequested', value)}
          step={0.01}
          primary={priceAllowed && problem.PriceRequested <= 0}
          disabled={!priceAllowed || disabled}
        />
      
        
        {/* Weight input - disabled if not allowed for selected problem type */}
        <CircleNumericInput
          value={WeightRequested}
          changeFunc={(value: number) => onProblemChange(problem.id, 'WeightRequested', value)}
          step={0.1}
          primary={weightAllowed && problem.WeightRequested <= 0}
          disabled={!weightAllowed || disabled}
        />
        
          {/* Quantity input - disabled if not allowed for selected problem type */}
       
          <CircleNumericInput
          value={QuantityRequested}
          changeFunc={(value: number) => onProblemChange(problem.id, 'QuantityRequested', value)}
          primary={quantityAllowed && problem.QuantityRequested <= 0}
          disabled={!quantityAllowed || disabled}
        />
      </InputsWrapper>
    </SearchBlockWithTitle>
  )
}

/**
 * Styled wrapper for input fields with responsive layout
 */
const InputsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  
  
  /* Visual feedback for disabled inputs */
  & > *[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      opacity: 0.5;
    }
  }
`

export default ProblemInputFields 